/**
 * Guided demo tour registry.
 *
 * Each tour is a linear sequence of steps walked while demo mode is on.
 * Steps hardcode demo-1 routes so every stop is served by the seeded demo
 * generators. Copy rules: never use em dashes; the platform is always
 * "SPARK Dash" (bare "SPARK" refers to the agency team only).
 */

/** The path portion of a step route, dropping any query string. A step's
 *  route may carry a query (e.g. ?audience=client) that must be applied on page
 *  entry, but usePathname()/location.pathname exclude the query — so every
 *  route-vs-pathname comparison (navigation gating in the tour runtime, the
 *  anchor-seek gate in the overlay) must compare on the path alone. */
export function routePath(route: string): string {
  return route.split("?")[0];
}

export type TourPlacement = "top" | "bottom" | "left" | "right" | "auto";

export interface TourStep {
  id: string;
  /** Absolute path; next()/back() push it when the pathname differs. */
  route: string;
  /** data-tour attribute value; omitted renders a centered card. */
  anchor?: string;
  /** Preferred tooltip side relative to the anchor. Default "auto". */
  placement?: TourPlacement;
  title: string;
  /** Benefit-led copy shown to the prospect; doubles as a presenter script. */
  body: string;
  /** Padding in px around the anchor rect. Default 8. */
  spotlightPadding?: number;
}

export type TourCategory = "onboarding" | "demo";

export interface Tour {
  id: string;
  name: string;
  /** Grouping on the launcher: internal staff onboarding vs client-facing demos. */
  category: TourCategory;
  /** Scenario blurb shown on the Guided Tours launcher card. */
  description: string;
  steps: TourStep[];
}

const MARKETING_CLIENT_TOUR: Tour = {
  id: "marketing-client",
  name: "Marketing Client Walkthrough",
  category: "demo",
  description:
    "The core pitch for a prospective marketing client: the weekly Pulse as the shared view, transparent campaign goals, the Health dashboard as an example deep dive, and the stored report library.",
  steps: [
    {
      id: "welcome",
      route: "/clients/demo-1/pulse/classic?audience=client",
      title: "Welcome to SPARK Dash",
      body: "SPARK Dash is where you and our team look at the same picture of your online presence: always current, always in one place. Let's take the two-minute tour.",
    },
    {
      id: "pulse-headline",
      route: "/clients/demo-1/pulse/classic?audience=client",
      anchor: "pulse-headline",
      placement: "bottom",
      title: "Pulse: your week at a glance",
      body: "Pulse is the shared view between you and our team. Every week it shows how everything looks over time, from momentum and wins to anything that needs attention, written in plain English rather than spreadsheets.",
    },
    {
      id: "pulse-board",
      route: "/clients/demo-1/pulse/classic?audience=client",
      anchor: "pulse-client-board",
      placement: "top",
      title: "Wins and clear next steps",
      body: "SPARK Dash doesn't stop at reporting. Pulse leads with your wins, then a short, honest list of what our team is working on, each with a note on what we're doing about it. It's always clear what happens next.",
    },
    {
      id: "pulse-campaigns",
      route: "/clients/demo-1/pulse/classic?audience=client",
      anchor: "pulse-campaigns",
      placement: "top",
      title: "Your goals, right where you look",
      body: "The campaigns we run for you report their own progress inside Pulse. You never have to ask how it's going; it's already answered.",
    },
    {
      id: "campaign-goals",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-goals",
      placement: "top",
      title: "Campaigns: shared goals, visible progress",
      body: "When we set a goal with you, whether it's traffic, conversions, rankings, or return on ad spend, SPARK Dash tracks it automatically against a target and a pace. Everyone sees the same progress, all the time.",
    },
    {
      id: "dashboard-tabs",
      route: "/clients/demo-1/dashboard/health",
      anchor: "dashboard-tabs",
      placement: "bottom",
      title: "Dashboards for every angle",
      body: "Behind Pulse sit live dashboards covering every facet of your presence. Here's one example: Health.",
    },
    {
      id: "health-uptime",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-uptime",
      placement: "top",
      title: "Health: the deep dive on your site itself",
      body: "SPARK Dash watches your site around the clock, with uptime, SSL, speed, security, and accessibility audits on a schedule. As your hosting partner, we spot problems before your customers do.",
    },
    {
      id: "reports",
      route: "/clients/demo-1/reports/demo-report-1",
      anchor: "report-cover",
      placement: "right",
      title: "Reports: updates our team makes for you",
      body: "On top of the live views, our team prepares polished reports for you. Every report is stored, so you can come back and review any of them at any point.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/reports/demo-report-1",
      title: "That's SPARK Dash",
      body: "One shared picture: your weekly Pulse, transparent campaign goals, deep-dive dashboards, and a permanent library of reports. Ready to see it with your own data?",
    },
  ],
};

const HOSTING_CLIENT_TOUR: Tour = {
  id: "hosting-client",
  name: "Hosting Client Walkthrough",
  category: "demo",
  description:
    "The pitch for a prospective hosting client: the weekly Pulse as the shared view, then a walk through the Health dashboard covering round-the-clock uptime monitoring, performance, security, and WordPress health, with a hop into the uptime detail for depth.",
  steps: [
    {
      id: "welcome",
      route: "/clients/demo-1/pulse/classic?audience=client",
      title: "Welcome to SPARK Dash",
      body: "When we host your site, you get more than a server. You get a whole team watching over it, and one shared window into how it's doing. Let's take the two-minute tour.",
    },
    {
      id: "pulse-headline",
      route: "/clients/demo-1/pulse/classic?audience=client",
      anchor: "pulse-headline",
      placement: "bottom",
      title: "Your site at a glance, every week",
      body: "Pulse is the shared view between you and our team. Every week it shows how your site is doing in plain English, and flags anything that needs attention before it becomes a problem.",
    },
    {
      id: "health-intro",
      route: "/clients/demo-1/dashboard/health",
      anchor: "dashboard-tabs",
      placement: "bottom",
      title: "Health: your site's wellbeing in one place",
      body: "Behind Pulse sit live dashboards. Health is the one we live in as your hosting partner: a single blended score across uptime, speed, security, and more, updated on a schedule.",
    },
    {
      id: "health-uptime",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-uptime",
      placement: "top",
      title: "Uptime: we know before you do",
      body: "We check your site around the clock, including its SSL certificate. If anything goes down, we're alerted first, so we can act before your customers ever notice.",
    },
    {
      id: "uptime-detail",
      route: "/clients/demo-1/dashboard/uptime",
      anchor: "uptime-hero",
      placement: "bottom",
      title: "The depth behind the number",
      body: "It's not just up or down. We track response times and keep a full incident history, so every outage is measured and explained, never guessed at.",
    },
    {
      id: "health-performance",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-performance",
      placement: "top",
      title: "Speed that keeps customers and Google happy",
      body: "We continuously score your site's speed with Lighthouse and Core Web Vitals. A fast site converts better and ranks better, so we keep an eye on it and act when it slips.",
    },
    {
      id: "health-security",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-security",
      placement: "top",
      title: "Security, checked continuously",
      body: "We audit your site's security posture on a schedule: HTTPS, security headers, firewall and CDN protection. Keeping your site hardened is part of hosting with us.",
    },
    {
      id: "health-wordpress",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-wordpress",
      placement: "top",
      title: "Your WordPress, kept current and safe",
      body: "For WordPress sites, we watch core and plugin health so nothing drifts out of date. Staying current is one of the biggest things that keeps a site secure and fast.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/dashboard/health",
      title: "That's SPARK Dash hosting",
      body: "One site, a whole team watching it: uptime, speed, security, and WordPress health, always current and always in one place. Ready to see it with your own site?",
    },
  ],
};

// ── Staff onboarding sequence ───────────────────────────────────────────────
// Five short, ordered tours that teach a new account manager how to read and
// navigate the platform. Orientation only (no creation flows), so every stop
// runs on read-only demo surfaces. Where a staff-only action appears, the copy
// flags it and points forward rather than demonstrating it.

const ONBOARDING_GETTING_AROUND_TOUR: Tour = {
  id: "onboarding-getting-around",
  name: "1. Getting Around SPARK Dash",
  category: "onboarding",
  description:
    "Start here. The lay of the land for a new team member: the client roster, opening a workspace, the icon rail, search, the company switcher, and what demo mode is.",
  steps: [
    {
      id: "welcome",
      route: "/",
      title: "Welcome to the team",
      body: "This is SPARK Dash, the platform where we track everything happening for our clients. This short series of tours will get you comfortable finding your way around. Everything you'll see is demo data, so explore freely.",
    },
    {
      id: "agency-roster",
      route: "/",
      anchor: "agency-clients",
      placement: "top",
      title: "Your client roster",
      body: "The home screen lists every client we look after, each with a quick read on their traffic, search, and site health. Click any card to open that client's workspace.",
    },
    {
      id: "sidebar",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "sidebar-nav",
      placement: "right",
      title: "Every client has a workspace",
      body: "Once you're inside a client, this rail is your map. Hover any icon to see what's inside it: Pulse, Campaigns, Publishing, Dashboards, Reports, and the audit tools all live here.",
    },
    {
      id: "search",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "global-search",
      placement: "bottom",
      title: "Search finds anything, from anywhere",
      body: "Click the search box or press Ctrl+K, then type. It finds companies, any page you have access to, records like reports and campaigns, and these help articles. Inside a client, their pages and records come first. Arrow keys move through the results and Enter opens one.",
    },
    {
      id: "switcher",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "client-switcher",
      placement: "bottom",
      title: "Jump between clients",
      body: "The company switcher in the top bar moves you straight to another client without going home. It works from anywhere in the app and remembers who you've looked at recently.",
    },
    {
      id: "demo-mode",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "workspace-tools",
      placement: "bottom",
      title: "Demo mode, and how to leave it",
      body: "You're in demo mode right now, so every name and number here is fictional and safe to click around. It stays on until you turn it off: open Workspace tools in the top bar to find the Demo toggle. Turn it off whenever you want to switch back to real client data.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/dashboard/overview",
      title: "You know your way around",
      body: "That's the lay of the land. You're still in demo mode, so keep exploring freely, and remember to click that toggle to switch it off when you want to see real data. Next up, learn how to read a client's dashboard.",
    },
  ],
};

const ONBOARDING_DASHBOARDS_TOUR: Tour = {
  id: "onboarding-dashboards",
  name: "2. Reading a Client Dashboard",
  category: "onboarding",
  description:
    "How to read the numbers: choosing a date range and comparison, reading metric cards and their change pills, and the six tabs that slice a client different ways.",
  steps: [
    {
      id: "intro",
      route: "/clients/demo-1/dashboard/overview",
      title: "Reading the dashboard",
      body: "Every client's dashboard pulls live data from their connected tools. Let's learn how to read one.",
    },
    {
      id: "date-range",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "date-range",
      placement: "bottom",
      title: "Pick your window",
      body: "Start by choosing a date range. The same control sets what you compare against: the previous period, the previous year, or nothing at all.",
    },
    {
      id: "metrics",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "overview-metrics",
      placement: "top",
      title: "Metrics and change",
      body: "Each card shows a headline number with a colored pill next to it. Green means it improved versus your comparison window, red means it slipped. That pill is usually the first thing you'll read.",
    },
    {
      id: "tabs",
      route: "/clients/demo-1/dashboard/overview",
      anchor: "dashboard-tabs",
      placement: "bottom",
      title: "Six angles on the same client",
      body: "These tabs slice the same client different ways: Performance, Acquisition, Engagement, AI, and Health. A dimmed tab just means that client hasn't connected data for it yet.",
    },
    {
      id: "deeper",
      route: "/clients/demo-1/dashboard/health",
      anchor: "health-uptime",
      placement: "top",
      title: "Going deeper",
      body: "Click any tab to dig in. Health, for example, shows uptime, speed, and security for the site itself. Every tab works the same way: skim the top, drill where you need to.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/dashboard/health",
      title: "That's a dashboard",
      body: "You can now read any client's dashboard. Next, the view you'll open most often: Pulse.",
    },
  ],
};

const ONBOARDING_PULSE_TOUR: Tour = {
  id: "onboarding-pulse",
  name: "3. The Weekly Pulse",
  category: "onboarding",
  description:
    "The heart of your weekly routine: the shared summary, the Team and Client views, the wins and needs-attention board, AI recommendations, campaigns, and where past weeks are saved.",
  steps: [
    {
      id: "headline",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-headline",
      placement: "bottom",
      title: "Where your week starts",
      body: "Pulse is the view you'll open most mornings. It sums up a client's week in plain English, so you can walk into any conversation already knowing the story.",
    },
    {
      id: "audience",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-audience",
      placement: "bottom",
      title: "Team View and Client View",
      body: "You'll work in Team View, the internal triage rendering. Flip to Client View to see exactly what a client sees: wins first, a short list of what we're working on with your action notes, and what's coming up.",
    },
    {
      id: "board",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-board",
      placement: "top",
      title: "Wins and what needs attention",
      body: "Pulse sorts the week into wins and things worth a closer look, so nothing important slips past you. This is your weekly triage list.",
    },
    {
      id: "recommendations",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-ai-recommendations",
      placement: "top",
      title: "Recommendations",
      body: "For clients with it enabled, Claude drafts prioritized recommendations here. Running and refining them is a staff task you'll pick up when you're working with a real client; for now, just know this is where they live.",
    },
    {
      id: "campaigns",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-campaigns",
      placement: "top",
      title: "Campaigns report in too",
      body: "Any campaigns we're running for the client show their progress right inside Pulse, so their goals stay front and center.",
    },
    {
      id: "history",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-nav",
      placement: "right",
      title: "Every week is saved",
      body: "Hover this Pulse icon and you'll find Pulse history: past Pulses are archived there, and for many clients each one also posts to a shared Slack channel. You can always look back at how a client's story has developed.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/pulse/classic?audience=team",
      title: "That's Pulse",
      body: "Pulse is the heartbeat of your week. Next, a closer look at Campaigns.",
    },
  ],
};

const ONBOARDING_CAMPAIGNS_TOUR: Tour = {
  id: "onboarding-campaigns",
  name: "4. Campaigns",
  category: "onboarding",
  description:
    "Reading campaign progress: what a campaign is, how goals track against a target and pace, the optional page, keyword, and ad trackers, and the staff-only Tasks panel.",
  steps: [
    {
      id: "list",
      route: "/clients/demo-1/campaigns",
      anchor: "campaigns-list",
      placement: "top",
      title: "Goals we're working toward",
      body: "A campaign is a set of goals we're tracking for a client over a stretch of time. Each card shows how far along it is and how many days are left.",
    },
    {
      id: "goals",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-goals",
      placement: "top",
      title: "Inside a campaign",
      body: "Open a campaign to see its goals. Each one tracks a metric against a target and a pace, so at a glance you can tell whether it's on track, at risk, or already achieved.",
    },
    {
      id: "trackers",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-trackers",
      placement: "top",
      title: "Optional deep trackers",
      body: "Some campaigns add focused trackers for specific pages, keywords, or ad campaigns, so you can watch the exact things that matter to the goal.",
    },
    {
      id: "tasks",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-tasks",
      placement: "top",
      title: "Tasks, for the team only",
      body: "This panel holds the team's action items for the campaign, and it's never shown to clients. Creating tasks is something you'll do once you're working with real clients.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      title: "That's Campaigns",
      body: "You can now read any campaign's progress. Last stop: Reports.",
    },
  ],
};

const ONBOARDING_REPORTS_TOUR: Tour = {
  id: "onboarding-reports",
  name: "5. Reports",
  category: "onboarding",
  description:
    "The report library and how clients see it: where saved and scheduled reports live, what a report looks like inside, its section navigation, and the public share link.",
  steps: [
    {
      id: "list",
      route: "/clients/demo-1/reports",
      anchor: "reports-list",
      placement: "top",
      title: "The report library",
      body: "Every report we generate for a client is saved here, along with any set to send on a schedule. Nothing gets lost, so you can always come back to an old one.",
    },
    {
      id: "cover",
      route: "/clients/demo-1/reports/demo-report-1",
      anchor: "report-cover",
      placement: "right",
      title: "Inside a report",
      body: "A report is a snapshot of a client's data at the moment it was made, laid out as a polished document. Each one has a public share link, so a client can open it in one click without logging in.",
    },
    {
      id: "nav",
      route: "/clients/demo-1/reports/demo-report-1",
      anchor: "report-nav",
      placement: "right",
      title: "Jump around",
      body: "Long reports have a sticky menu down the side, so you can jump straight to any section: search, ads, calls, whatever the client cares about.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/reports/demo-report-1",
      title: "You're oriented",
      body: "That's the full tour. You now know your way around SPARK Dash: the roster, dashboards, Pulse, campaigns, and reports. When you're ready to start doing the work, your team will walk you through creating these with real clients.",
    },
  ],
};

// ── Staff onboarding: workflow / how-to tours (phase 2) ─────────────────────
// Guided-pointer tours for the core "doing" tasks. The mutation builders aren't
// walked (they're data-dependent and redirected in demo); instead each tour
// spotlights the entry point (rendered inert in demo via DemoInertButton) and
// explains the flow, pointing at where the result lands.

const ONBOARDING_REPORTS_HOWTO_TOUR: Tour = {
  id: "onboarding-reports-howto",
  name: "6. Creating Reports",
  category: "onboarding",
  description:
    "Walk the real report builder: where a report starts, choosing what goes in, generating once or on a schedule, and where finished reports live.",
  steps: [
    {
      id: "intro",
      route: "/clients/demo-1/reports",
      title: "Creating a report",
      body: "Reports package a client's data into a polished, shareable document. Let's build one together.",
    },
    {
      id: "start",
      route: "/clients/demo-1/reports",
      anchor: "reports-new",
      placement: "bottom",
      title: "Start a new report",
      body: "You start a report from this button. Let's open the builder and look at it.",
    },
    {
      id: "builder",
      route: "/clients/demo-1/reports/new",
      anchor: "report-builder",
      placement: "top",
      title: "Choose what goes in",
      body: "This is the builder. You give it a title and date range, pick a template, then choose exactly which integrations and sections to include. Toggle any of these on or off.",
    },
    {
      id: "schedule",
      route: "/clients/demo-1/reports/new",
      anchor: "report-schedule",
      placement: "top",
      title: "Once, or on a schedule",
      body: "Then choose delivery: generate it once now, or set it to send itself on a recurring schedule, weekly or monthly, straight to the client.",
    },
    {
      id: "library",
      route: "/clients/demo-1/reports",
      anchor: "reports-list",
      placement: "top",
      title: "Where reports live",
      body: "Every report you generate lands in this library, and scheduled ones reappear here each time they send. You can reopen or reshare any of them at any point.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/reports",
      title: "That's reporting",
      body: "You've seen the whole report flow. Next, campaigns.",
    },
  ],
};

const ONBOARDING_CAMPAIGNS_HOWTO_TOUR: Tour = {
  id: "onboarding-campaigns-howto",
  name: "7. Building a Campaign",
  category: "onboarding",
  description:
    "Walk the real campaign builder: the campaign shell, the goal builder with its live preview, and how saved goals then track their own progress.",
  steps: [
    {
      id: "intro",
      route: "/clients/demo-1/campaigns",
      title: "Building a campaign",
      body: "Campaigns set goals for a client and track them automatically over time. Let's build one together.",
    },
    {
      id: "start",
      route: "/clients/demo-1/campaigns",
      anchor: "campaigns-new",
      placement: "bottom",
      title: "Start a campaign",
      body: "You start a campaign from this button. Let's open the builder to see what goes into one.",
    },
    {
      id: "shell",
      route: "/clients/demo-1/campaigns/demo-camp-1/edit",
      anchor: "campaign-form",
      placement: "bottom",
      title: "The campaign itself",
      body: "A campaign holds a name, a start and target date, a status, and the optional page, keyword, and ad trackers you can switch on.",
    },
    {
      id: "goal-builder",
      route: "/clients/demo-1/campaigns/demo-camp-1/edit",
      anchor: "goal-builder",
      placement: "top",
      title: "Building a goal",
      body: "This is the goal builder. Pick a metric, choose a goal type (reach a value, change by a percent, cross a threshold, or hold a line), set a target, and scope it to a page, keyword, or ad campaign if you want. The preview card updates live as you go.",
    },
    {
      id: "progress",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-goals",
      placement: "top",
      title: "Goals track themselves",
      body: "Once saved, each goal tracks itself against its target and expected pace, and shows progress like this. Nothing to update by hand.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      title: "That's campaigns",
      body: "You've built a campaign and seen how its goals track. Last stop: the Pulse AI tools.",
    },
  ],
};

const ONBOARDING_PULSE_AI_TOUR: Tour = {
  id: "onboarding-pulse-ai",
  name: "8. Working with Pulse AI",
  category: "onboarding",
  description:
    "The full AI workflow, staff-only: generate recommendations, push one into a campaign as a task, and see the real task brief and Deep Dive result.",
  steps: [
    {
      id: "intro",
      route: "/clients/demo-1/pulse/classic?audience=team",
      title: "The AI workflow",
      body: "For clients with AI enabled, Pulse goes beyond reporting: it drafts recommendations and helps you turn them into action. Here's the whole flow.",
    },
    {
      id: "generate",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-ai-generate",
      placement: "bottom",
      title: "Generate recommendations",
      body: "You generate a fresh set of recommendations here. It runs in the background for the period you're viewing, and the weekly job also produces these automatically.",
    },
    {
      id: "review",
      route: "/clients/demo-1/pulse/classic?audience=team",
      anchor: "pulse-ai-recommendations",
      placement: "top",
      title: "Review the recommendations",
      body: "Each recommendation is specific and prioritized, sometimes with a supporting chart. When one is worth acting on, you push it into a campaign as a task. Let's follow one there.",
    },
    {
      id: "push",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-tasks",
      placement: "top",
      title: "It becomes a tracked task",
      body: "Pushed recommendations land here in the campaign's staff-only Tasks panel, as tracked action items. Here's one that was pushed in.",
    },
    {
      id: "brief",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "campaign-task-brief",
      placement: "top",
      title: "The task brief",
      body: "Every task can carry a full brief: a role, an hours estimate, and a clear what-to-do and why. This is what you hand to whoever does the work.",
    },
    {
      id: "deep-dive",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      anchor: "deep-dive-result",
      placement: "top",
      title: "The Deep Dive result",
      body: "Run a Deep Dive and Claude returns a refined, data-grounded plan backed by exact evidence, shown right here. It's the difference between a good idea and a plan someone can execute.",
    },
    {
      id: "closing",
      route: "/clients/demo-1/campaigns/demo-camp-1",
      title: "That's the full workflow",
      body: "That's the AI workflow, all staff-only. You've now seen how SPARK Dash fits together, from a client's weekly Pulse to tracked, evidence-backed work.",
    },
  ],
};

export const TOURS: Tour[] = [
  ONBOARDING_GETTING_AROUND_TOUR,
  ONBOARDING_DASHBOARDS_TOUR,
  ONBOARDING_PULSE_TOUR,
  ONBOARDING_CAMPAIGNS_TOUR,
  ONBOARDING_REPORTS_TOUR,
  ONBOARDING_REPORTS_HOWTO_TOUR,
  ONBOARDING_CAMPAIGNS_HOWTO_TOUR,
  ONBOARDING_PULSE_AI_TOUR,
  MARKETING_CLIENT_TOUR,
  HOSTING_CLIENT_TOUR,
];

export function getTour(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id);
}
